import { UPDATE_PRIORITY, type Application } from 'pixi.js';

// The readout in the title bar: frame rate, main-thread time, GPU time and JS heap.
// A page cannot see the machine's own CPU, GPU or RAM figures, so these are this tab's:
// CPU is the script time each frame takes, as a share of the frame; GPU is the 3D scene's
// draw time, from a WebGL timer query where the browser offers one; RAM is the JS heap,
// which only Chromium reports.

/** How often the readout refreshes, in ms; per-frame numbers are averaged over it. */
const REFRESH_MS = 500;

interface Ext { TIME_ELAPSED_EXT: number; GPU_DISJOINT_EXT: number }

/** Times GPU work between begin and end with EXT_disjoint_timer_query_webgl2; results land a few frames late. */
export class GpuTimer {
  private readonly ext: Ext | null;
  private readonly pending: WebGLQuery[] = [];
  private active = false;
  private sum = 0;
  private count = 0;

  constructor(private readonly gl: WebGL2RenderingContext) {
    this.ext = gl.getExtension('EXT_disjoint_timer_query_webgl2') as Ext | null;
  }

  get supported(): boolean {
    return this.ext !== null;
  }

  begin(): void {
    // keep a short queue: if results stop coming back, stop asking
    if (!this.ext || this.pending.length > 4) return;
    const q = this.gl.createQuery();
    if (!q) return;
    this.gl.beginQuery(this.ext.TIME_ELAPSED_EXT, q);
    this.pending.push(q);
    this.active = true;
  }

  end(): void {
    if (!this.ext) return;
    if (this.active) this.gl.endQuery(this.ext.TIME_ELAPSED_EXT);
    this.active = false;
    this.poll();
  }

  private poll(): void {
    const gl = this.gl, ext = this.ext!;
    const disjoint = gl.getParameter(ext.GPU_DISJOINT_EXT) as boolean;
    while (this.pending.length > 0) {
      const q = this.pending[0]!;
      if (!gl.getQueryParameter(q, gl.QUERY_RESULT_AVAILABLE)) break;
      if (!disjoint) {
        const ms = (gl.getQueryParameter(q, gl.QUERY_RESULT) as number) / 1e6;
        this.sum += ms; this.count++;
      }
      gl.deleteQuery(q);
      this.pending.shift();
    }
  }

  /** Mean ms per timed frame since the last take, or null if none came back. */
  take(): number | null {
    if (this.count === 0) return null;
    const ms = this.sum / this.count;
    this.sum = 0; this.count = 0;
    return ms;
  }
}

interface ChromeMemory { usedJSHeapSize: number; jsHeapSizeLimit: number }

const PERF_KEY = 'thrash.perf';

/** Whether the readout is wanted: `?perf` in the address, or the setting (Shift+F3). */
export function perfEnabled(): boolean {
  try { return new URLSearchParams(location.search).has('perf') || localStorage.getItem(PERF_KEY) === '1'; } catch { return false; }
}

export function setPerfEnabled(on: boolean): void {
  try { localStorage.setItem(PERF_KEY, on ? '1' : '0'); } catch { /* storage unavailable */ }
}

/** Fills `el` with the readout, measuring around everything the Pixi ticker runs each frame. */
export function startPerfReadout(app: Application, el: HTMLElement, gpu: GpuTimer | null): void {
  let frameStart = 0;
  let busy = 0;
  let frames = 0;
  let windowStart = performance.now();

  app.ticker.add(() => { frameStart = performance.now(); }, null, UPDATE_PRIORITY.INTERACTION + 100);
  app.ticker.add(() => {
    const now = performance.now();
    busy += now - frameStart;
    frames++;
    const span = now - windowStart;
    if (span < REFRESH_MS) return;

    const fps = frames * 1000 / span;
    const frameMs = span / frames;
    const cpuMs = busy / frames;
    const parts = [`${Math.round(fps)} FPS`, `CPU ${cpuMs.toFixed(1)} ms (${pct(cpuMs, frameMs)})`];
    if (gpu) {
      const gpuMs = gpu.take();
      parts.push(gpuMs !== null ? `GPU ${gpuMs.toFixed(1)} ms (${pct(gpuMs, frameMs)})` : gpu.supported ? 'GPU …' : 'GPU n/a');
    }
    const mem = (performance as Performance & { memory?: ChromeMemory }).memory;
    if (mem) parts.push(`RAM ${Math.round(mem.usedJSHeapSize / 2 ** 20)} MB`);
    el.textContent = parts.join(' · ');

    busy = 0; frames = 0; windowStart = now;
  }, null, UPDATE_PRIORITY.UTILITY - 100);
}

function pct(ms: number, frameMs: number): string {
  return `${Math.min(100, Math.round(ms / frameMs * 100))}%`;
}
